import axios, { AxiosError } from 'axios';
import { useAuthStore } from '@/stores/authStore';

const API_URL = import.meta.env.VITE_API_URL || '/api';

export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add auth token
api.interceptors.request.use((config) => {
  const { accessToken } = useAuthStore.getState();
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

// Response interceptor - handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as any;

    // If unauthorized and not already retrying
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const { refreshToken, setAccessToken, logout } = useAuthStore.getState();

      if (refreshToken) {
        try {
          const response = await axios.post(`${API_URL}/auth/refresh`, {
            refreshToken,
          });

          const { accessToken } = response.data;
          setAccessToken(accessToken);

          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return api(originalRequest);
        } catch (refreshError: any) {
          // Only logout if refresh token is explicitly rejected (401/403)
          // Don't logout on network errors or other failures
          if (refreshError?.response?.status === 401 || refreshError?.response?.status === 403) {
            console.log('Refresh token rejected, logging out');
            logout();
            window.location.href = '/login';
          } else {
            console.error('Token refresh failed (not logging out):', refreshError);
          }
          return Promise.reject(refreshError);
        }
      }
      // No refresh token - but don't auto-logout, let the UI handle it
      console.log('No refresh token available');
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  login: async (email: string, password: string) => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },

  logout: async () => {
    await api.post('/auth/logout');
  },

  getMe: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },
};

// Sales API
export const salesApi = {
  getSummary: async (params: {
    preset?: string;
    startDate?: string;
    endDate?: string;
    agentId?: string;
  }) => {
    const response = await api.get('/sales/summary', { params });
    return response.data;
  },

  getTransactions: async (params: {
    preset?: string;
    startDate?: string;
    endDate?: string;
    agentId?: string;
    page?: number;
    limit?: number;
  }) => {
    const response = await api.get('/sales/transactions', { params });
    return response.data;
  },

  getChartData: async (params: {
    preset?: string;
    startDate?: string;
    endDate?: string;
    groupBy?: 'day' | 'week' | 'month';
  }) => {
    const response = await api.get('/sales/chart-data', { params });
    return response.data;
  },

  getRecent: async (limit = 10) => {
    const response = await api.get('/sales/recent', { params: { limit } });
    return response.data;
  },
};

// Agents API
export const agentsApi = {
  getAll: async () => {
    const response = await api.get('/agents');
    return response.data;
  },

  getById: async (id: string) => {
    const response = await api.get(`/agents/${id}`);
    return response.data;
  },
};

// Settings API
export const settingsApi = {
  getAll: async () => {
    const response = await api.get('/settings');
    return response.data;
  },

  update: async (key: string, value: unknown) => {
    const response = await api.patch('/settings', { key, value });
    return response.data;
  },

  bulkUpdate: async (settings: Record<string, unknown>) => {
    const response = await api.put('/settings', settings);
    return response.data;
  },
};

// Sync API
export const syncApi = {
  getStatus: async () => {
    const response = await api.get('/sync/status');
    return response.data;
  },

  trigger: async (type: 'full' | 'incremental' = 'incremental') => {
    const response = await api.post('/sync/trigger', { type });
    return response.data;
  },

  getLogs: async (params: { page?: number; limit?: number }) => {
    const response = await api.get('/sync/logs', { params });
    return response.data;
  },
};
